import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_LEGACY_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER,
  MatLegacyAutocompleteModule,
  MatLegacyAutocompleteSelectedEvent,
  MatLegacyAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MAT_LEGACY_SELECT_SCROLL_STRATEGY_PROVIDER } from '@angular/material/legacy-select';
import { Names, Values, View } from '@mode-switch/model';
import { BasicChannel } from '@mode-vegas/vegas';
import { IconComponent, trackByFactory } from '@mode/capra';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { getFieldIcon } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledAttr, Switch } from '@mode/shared/util-switch';
import { ModeRole } from 'libs/shared/contract-common/src/lib/flamingo.types';
import { Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';

export interface AttrsList {
  attr: CompiledAttr;
  icon: string;
  discrete: boolean;
}

@Component({
  standalone: true,
  selector: 'mode-typeahead-attrs-list',
  imports: [CommonModule, IconComponent, MatLegacyMenuModule, MatLegacyAutocompleteModule, ReactiveFormsModule],
  templateUrl: './typeahead-attrs-list.component.html',
  styleUrls: ['./typeahead-attrs-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MAT_LEGACY_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER, MAT_LEGACY_SELECT_SCROLL_STRATEGY_PROVIDER],
})
export class TypeaheadAttrsListComponent implements OnInit, OnChanges {
  @Input() view!: FlamingoTypes.ModeView;
  @Input() chartType!: ChartTypes.ChartType;
  @Input() channel?: BasicChannel;
  @Input() placeholderText = '';
  @Output() attrSelected = new EventEmitter();
  @ViewChildren('searchInput') searchInput: QueryList<ElementRef> | undefined;
  @ViewChild(MatLegacyAutocompleteTrigger) autocompleteTrigger: MatLegacyAutocompleteTrigger | undefined;

  search = new FormControl<string>('', { nonNullable: true });
  attrsList: AttrsList[] = [];
  filteredOptions$: Observable<ReadonlyArray<AttrsList>> | undefined;
  icon: 'icon-close' | 'icon-search' = 'icon-search';
  trackByName = trackByFactory<CompiledAttr>(({ attrName }) => attrName);

  constructor(private analyticsService: AnalyticsService) {}

  ngOnInit(): void {
    this.filteredOptions$ = this.search.valueChanges.pipe(
      startWith(''),
      tap((value) => {
        this.icon = value ? 'icon-close' : 'icon-search';
      }),
      map((value) => this._filter(value))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.view || changes.chartType) {
      this.attrsList = this.buildCompileAttrs();
      this.search.setValue('');
    }
  }

  private _filter(value: string | undefined): ReadonlyArray<AttrsList> {
    if (!value) {
      return this.attrsList;
    }
    const filterValue = value.toLowerCase();

    return this.attrsList.filter((attr) => {
      const name = attr.attr.attrName;
      if (name && name.toLowerCase().includes(filterValue)) {
        return attr;
      }
    });
  }

  onIconClick(): void {
    if (this.icon === 'icon-close') {
      this.search.setValue('');
      this.searchInput?.first.nativeElement.focus();
    }
  }

  onTypeaheadMenuOpened(): void {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Typeahead Menu Opened',
      chartType: this.chartType,
      resource: AnalyticsEvent.Context.Visualization,
    });
  }

  getSortedCompiledAttrs(attrs: View.Attr<ModeRole>[]): CompiledAttr[] {
    const sortByAttrName = (a1: CompiledAttr, a2: CompiledAttr): number => {
      if (!a1.attrName) {
        return -1;
      }
      if (!a2.attrName) {
        return 1;
      }
      return a1.attrName.localeCompare(a2.attrName);
    };

    return attrs.map((attr) => CompiledAttr.fromAttr(this.view, attr)).sort(sortByAttrName);
  }

  buildCompileAttrs() {
    const model = Switch.Models.makeFromView(this.view.name, this.view);
    const dimensions = Switch.Attrs.dimensions(model, this.view.name);
    const measures = Switch.Attrs.measures(model, this.view.name);
    const valuesAttr = CompiledAttr.fromAttr(this.view, Values.make());
    const namesAttr = CompiledAttr.fromAttr(this.view, Names.make());
    const isVegas = this.chartType === 'vegas';
    const isUnsupportedChannel = this.channel === 'filter' || this.channel === 'value';

    const sortedDimensions = this.getSortedCompiledAttrs([...dimensions]);
    const sortedMeasures = this.getSortedCompiledAttrs([...measures]);

    // We need to hide measure names and values on Quick Charts and Pivot Table Quick Chart
    const attrs = [
      ...(isVegas && !isUnsupportedChannel ? [namesAttr] : []),
      ...sortedDimensions,
      ...(isVegas && !isUnsupportedChannel ? [valuesAttr] : []),
      ...sortedMeasures,
    ];

    return attrs
      .filter((attr: CompiledAttr) => attr.isValid)
      .map((attr: CompiledAttr) => {
        return {
          attr,
          icon: getFieldIcon(attr.formulaDataType, attr.isCalc),
          discrete: attr.isDiscrete,
        };
      });
  }

  onOptionSelected($event: MatLegacyAutocompleteSelectedEvent) {
    const { option } = $event;
    const attr = this.attrsList.find((attr) => attr.attr.attrName === option.value);
    if (attr) {
      this.attrSelected.emit({
        event: $event,
        attr: attr.attr,
        onFinalize: () => this.autocompleteTrigger?.closePanel(),
      });
      this.search.reset();
    }

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Typeahead Field Selected',
      chartType: this.chartType,
      resource: AnalyticsEvent.Context.Visualization,
    });
  }
}
